// Door-range browser — search, style/finish filters, range cards, lightbox.
//
// Extracted from step-2-style.ts on 2026-08-10 so the same browser can power a
// standalone "Our Kitchens" page on a retailer's site without a second copy of
// this code. Nothing here knows about the wizard: the host supplies the current
// selection and receives callbacks, via RangeBrowserHost below. A browse-only
// page simply omits `selectRange`, and no Select CTA is rendered.

import type { DoorRange } from "../types";
import { escape, escapeAttr } from "./index";
import { observeRangeCard, track } from "./analytics";
import { svgIcon } from "./icons";

/** What the embedding page must provide. */
export interface RangeBrowserHost {
  /**
   * Which retailer's wizard this is. Sent as ?retailer= so the API returns only
   * the suppliers enabled for them (2026-08-17). Optional: omit it on a
   * browse-only page and you get every published range, which is also what a
   * retailer with no supplier selection configured gets.
   */
  retailerId?: string;
  /** The range + colour currently chosen, so cards can render as selected. */
  getSelection: () => { rangeId: string; colourName: string };
  /** Commit a selection. OMIT on a browse-only page — the Select This Style
   *  CTA (card and lightbox) is then not rendered at all. */
  selectRange?: (range: NormalisedRange, colourName: string) => void;
  /** A different swatch was picked on the already-selected card. */
  changeColour?: (range: NormalisedRange, colourName: string) => void;
  /** Fires after a selection commits — the wizard scrolls on to the next
   *  section here; a browse page would leave this out. */
  afterSelect?: () => void;
}

export interface RangeBrowserHandle {
  /** Re-render the grid (e.g. after the host's selection changed elsewhere). */
  refresh: () => void;
}

interface BrowserState {
  ranges: NormalisedRange[];
  loading: boolean;
  error: string | null;
  search: string;
  styleSel: Set<string>;
  finishSel: Set<string>;
  /** Supplier slug tab in effect, or null for "all suppliers". */
  supplierSel: string | null;
}

/** One supplier tab, derived from whatever the API actually returned. */
interface SupplierTab {
  slug: string;
  label: string;
  count: number;
}

const STYLE_FILTERS = ["Slab", "Handleless", "J Pull", "Shaker", "Inframe"] as const;
const FINISH_FILTERS = ["Gloss", "Matt", "Painted", "Wood Grain", "Metallic", "Foil"] as const;
// ── Icons for the visual pickers (2026-08-10) ────────────────────────────────
// Door style, finish and appliance type are VISUAL attributes — a word alone
// makes a customer guess what "Inframe" or "J Pull" looks like. Budget and
// timeframe stay plain text pills on step 3: there's nothing to depict, and an
// icon there would be decoration pretending to be information.
//
// Hand-rolled, 24×24, 1.5px stroke, no icon library (house design rules).

/** Door front styles, drawn as the door itself seen face-on. */
const STYLE_ICONS: Record<string, string> = {
  "Slab": svgIcon(`<rect x="5" y="3" width="14" height="18" rx="1"/>`),
  "Handleless": svgIcon(`<rect x="5" y="6" width="14" height="15" rx="1"/><path d="M5 4.5h14"/>`),
  "J Pull": svgIcon(`<rect x="5" y="3" width="14" height="18" rx="1"/><path d="M8 6.5h8a1.5 1.5 0 0 1-1.5 1.5H8z"/>`),
  "Shaker": svgIcon(`<rect x="5" y="3" width="14" height="18" rx="1"/><rect x="8" y="6.5" width="8" height="11" rx=".5"/>`),
  "Inframe": svgIcon(`<rect x="3" y="2" width="18" height="20" rx="1"/><rect x="6.5" y="5.5" width="11" height="13" rx=".5"/>`),
};

/** Finishes, drawn as the light behaviour that distinguishes them. */
const FINISH_ICONS: Record<string, string> = {
  "Gloss": svgIcon(`<rect x="4" y="4" width="16" height="16" rx="1.5"/><path d="M8 18 16 6"/><path d="M11.5 18 15 13"/>`),
  "Matt": svgIcon(`<rect x="4" y="4" width="16" height="16" rx="1.5"/><path d="M8 9h.01M12 9h.01M16 9h.01M8 13h.01M12 13h.01M16 13h.01M8 17h.01M12 17h.01M16 17h.01"/>`),
  "Painted": svgIcon(`<path d="M5 8V5.5A1.5 1.5 0 0 1 6.5 4h11A1.5 1.5 0 0 1 19 5.5V8z"/><path d="M5 8h14v3H5z"/><path d="M12 11v3"/><rect x="10" y="14" width="4" height="6" rx="1"/>`),
  "Wood Grain": svgIcon(`<rect x="4" y="4" width="16" height="16" rx="1.5"/><path d="M7 6c1.6 2.4 1.6 9.6 0 12"/><path d="M11 6c1.6 2.4 1.6 9.6 0 12"/><path d="M15 6c1.6 2.4 1.6 9.6 0 12"/>`),
  "Metallic": svgIcon(`<rect x="4" y="4" width="16" height="16" rx="1.5"/><path d="M8 4v16M12 4v16M16 4v16"/>`),
  "Foil": svgIcon(`<path d="M4 4h11l5 5v11H4z"/><path d="M15 4v5h5"/><path d="M7 16c2-2.5 5-2.5 7 0"/>`),
};
const EXCLUDE_NAMES = ["coniston"];

// ── Mount ────────────────────────────────────────────────────────────────────

/** Render the browser into `host` and start loading the catalogue. Safe to call
 *  on a host that is later thrown away — everything is scoped to the subtree. */
export function mountRangeBrowser(host: HTMLElement, api: RangeBrowserHost): RangeBrowserHandle {
  const local: BrowserState = {
    ranges: [],
    loading: true,
    error: null,
    search: "",
    styleSel: new Set<string>(),
    finishSel: new Set<string>(),
    supplierSel: null,
  };

  host.innerHTML = `
    <div class="kos-tkc-supplier-tabs" data-role="supplier-tabs"></div>

    <div class="kos-tkc-search-wrap">
      <input
        class="kos-tkc-search"
        type="search"
        placeholder="Search door ranges or colours…"
        aria-label="Search door ranges or colours"
        data-role="search"
        value=""
      />
    </div>

    <div class="kos-tkc-filter-row">
      <div class="kos-tkc-filter-label">Filter By Style</div>
      <div class="kos-tkc-pills" data-role="style-pills">
        ${STYLE_FILTERS.map((s) => iconPill(s, false, STYLE_ICONS)).join("")}
      </div>
    </div>

    <div class="kos-tkc-filter-row">
      <div class="kos-tkc-filter-label">Filter By Finish</div>
      <div class="kos-tkc-pills" data-role="finish-pills">
        ${FINISH_FILTERS.map((s) => iconPill(s, false, FINISH_ICONS)).join("")}
      </div>
    </div>

    <div class="kos-tkc-count" data-role="count"></div>
    <div data-role="grid-host"></div>
  `;

  // Supplier tabs are rendered from the data, not hardcoded. With a single
  // supplier there is nothing to choose between, so we show one plain label
  // (visually identical to the old fixed "TKC" tab). Only when a retailer has
  // more than one supplier enabled does an "All" tab appear and the tabs
  // become interactive.
  const tabsHost = host.querySelector<HTMLElement>("[data-role=supplier-tabs]")!;

  const renderSupplierTabs = (): void => {
    const tabs = supplierTabs(local.ranges);
    if (tabs.length === 0) { tabsHost.innerHTML = ""; return; }

    const btn = (slug: string | null, label: string, count: number): string => {
      const active = local.supplierSel === slug;
      return `<button type="button" class="kos-tkc-supplier-tab${active ? " kos-active" : ""}"`
        + ` data-supplier="${slug ?? ""}"${tabs.length === 1 ? " disabled" : ""}>`
        + `${escape(label)} <span class="kos-tkc-supplier-tab__count">${count}</span></button>`;
    };

    tabsHost.innerHTML = tabs.length === 1
      ? btn(null, tabs[0]!.label, tabs[0]!.count)
      : [btn(null, "All", local.ranges.length)]
          .concat(tabs.map((t) => btn(t.slug, t.label, t.count)))
          .join("");
  };

  tabsHost.addEventListener("click", (e) => {
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>("[data-supplier]");
    if (!b || b.disabled) return;
    local.supplierSel = b.dataset.supplier ? b.dataset.supplier : null;
    renderSupplierTabs();
    refresh();
  });

  const refresh = (): void => renderGrid(host, api, local);

  const searchInput = host.querySelector<HTMLInputElement>("[data-role=search]")!;
  searchInput.addEventListener("input", () => {
    local.search = searchInput.value;
    refresh();
  });

  const wirePills = (role: string, set: Set<string>): void => {
    host.querySelector<HTMLElement>(`[data-role=${role}]`)!.addEventListener("click", (e) => {
      const p = (e.target as HTMLElement).closest<HTMLButtonElement>("[data-pill]");
      if (!p) return;
      const v = p.dataset.pill!;
      toggleSet(set, v);
      p.classList.toggle("kos-active", set.has(v));
      refresh();
    });
  };
  wirePills("style-pills", local.styleSel);
  wirePills("finish-pills", local.finishSel);

  refresh();

  void loadRanges(api.retailerId)
    .then((ranges) => {
      local.ranges = ranges;
      local.loading = false;
      renderSupplierTabs();
      refresh();
    })
    .catch((err: unknown) => {
      local.loading = false;
      local.error = err instanceof Error ? err.message : "Could not load styles.";
      refresh();
    });

  return { refresh };
}

export interface NormalisedColour {
  name: string;
  hex: string | null;
  availability: string;
  hero: string;       // portfolio image for this colour, or range hero
  closeUp: string;    // close-up for this colour, or range close-up
}

export interface NormalisedRange {
  id: string;
  name: string;
  /** Supplier slug (lower-case) — carried through for analytics tracking. */
  supplierId: string;
  /**
   * The supplier's consumer-facing brand, when one is set — e.g. TKC
   * Components' door collection sells as "On Trend". Empty string when the
   * supplier has no separate brand, in which case the tab falls back to the
   * slug. Resolved live from the API per request, NOT read from the range's
   * stored `supplierName`, so a rename needs no backfill.
   */
  supplierLabel: string;
  description: string;
  finishType: string;
  priceGroup: string;
  subChips: string[];
  tagPool: string[];          // pre-lowercased, alphanumeric-only tags for filtering
  hero: string;
  closeUp: string;
  colours: NormalisedColour[];
  paintToOrder: { enabled?: boolean; label?: string } | null;
  specs: { material: string; finish: string; style: string; thickness: string };
}

async function loadRanges(retailerId?: string): Promise<NormalisedRange[]> {
  // Which suppliers a retailer shows is now DATA, not code. Before 2026-08-17
  // this function hardcoded `supplierId === "tkc"`, so every retailer saw the
  // demo supplier's ranges and nothing else. The server now decides, from the
  // wizard grants in supplier_catalogue_access; a retailer with no selection
  // configured still gets everything published, so this is not a behaviour
  // change for anyone until an admin curates their list.
  const qs = retailerId ? `?retailer=${encodeURIComponent(retailerId)}` : "";
  const res = await fetch(`/api/door-ranges${qs}`, {
    credentials: "omit",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const body = (await res.json()) as {
    ranges?: DoorRange[];
    suppliers?: { slug?: string; name?: string; displayName?: string | null }[];
  };
  const all = Array.isArray(body.ranges) ? body.ranges : [];
  // slug → consumer-facing brand. Only suppliers that have one appear here;
  // everything else falls back to the existing label in supplierTabs().
  const labels = new Map<string, string>();
  for (const s of body.suppliers ?? []) {
    const slug = (s?.slug || "").toLowerCase();
    const label = (s?.displayName || "").trim();
    if (slug && label) labels.set(slug, label);
  }
  // Still consumer-facing: published ranges only, never drafts. (The API also
  // enforces this for anonymous callers — belt and braces, since this file is
  // also used by browse-only pages.)
  const live = all.filter((r) => {
    if (r.status && r.status !== "live" && r.status !== "approved") return false;
    const n = (r.name || "").toLowerCase();
    return !EXCLUDE_NAMES.some((ex) => n.includes(ex));
  });
  return live
    .map((r) => normaliseRange(r, labels))
    .filter((r): r is NormalisedRange => r !== null);
}

/** Supplier tabs derived from the loaded ranges, in stable alphabetical order. */
function supplierTabs(ranges: NormalisedRange[]): SupplierTab[] {
  const by = new Map<string, SupplierTab>();
  for (const r of ranges) {
    const slug = (r.supplierId || "").toLowerCase();
    if (!slug) continue;
    const hit = by.get(slug);
    if (hit) { hit.count += 1; continue; }
    // A customer sees the collection's brand where the supplier has set one —
    // TKC Components sells its doors as "On Trend". Without one we keep the
    // long-standing slug label rather than exposing the internal record name,
    // which is a trade-facing legal entity, not something to show a homeowner.
    by.set(slug, { slug, label: r.supplierLabel || slug.toUpperCase(), count: 1 });
  }
  return [...by.values()].sort((a, b) => a.label.localeCompare(b.label));
}

function normaliseRange(
  raw: DoorRange,
  supplierLabels?: ReadonlyMap<string, string>,
): NormalisedRange | null {
  if (!raw || !raw.name) return null;

  const closeUpsByColour = new Map<string, string>();
  const closeUpsList: string[] = [];
  (raw.doorCloseUps || []).forEach((c) => {
    if (!c || !c.url) return;
    closeUpsList.push(c.url);
    if (c.colourName) closeUpsByColour.set(c.colourName.toLowerCase(), c.url);
  });

  const portfolioByColour = new Map<string, string>();
  const portfolioList: string[] = [];
  (raw.portfolioImages || []).forEach((p) => {
    if (!p || !p.url) return;
    portfolioList.push(p.url);
    if (p.colourName) portfolioByColour.set(p.colourName.toLowerCase(), p.url);
  });

  const heroImage = raw.heroImage || portfolioList[0] || "";
  const defaultCloseUp = raw.doorCloseUp || closeUpsList[0] || heroImage;

  let colours: NormalisedColour[] = (raw.colours || []).map((c) => {
    const key = (c.name || "").toLowerCase();
    return {
      name: c.name || "",
      hex: c.hex || null,
      availability: c.availability || "",
      hero: portfolioByColour.get(key) || heroImage,
      closeUp: closeUpsByColour.get(key) || defaultCloseUp,
    };
  });
  if (colours.length === 0) {
    colours = [{
      name: raw.heroImageColour || raw.name,
      hex: null,
      availability: "",
      hero: heroImage,
      closeUp: defaultCloseUp,
    }];
  }

  const tagPool: string[] = [];
  (raw.subCategories || []).forEach((s) => { if (s) tagPool.push(s); });
  (raw.styleTags || []).forEach((s) => { if (s) tagPool.push(s); });
  if (raw.finishType) tagPool.push(raw.finishType);
  if (raw.material)   tagPool.push(raw.material);
  const tagPoolLc = tagPool.map((t) => t.toLowerCase().replace(/[^a-z0-9]+/g, ""));

  const subChips: string[] = [];
  const seen = new Set<string>();
  (raw.subCategories || []).forEach((s) => {
    if (!s) return;
    const k = s.toLowerCase();
    if (seen.has(k)) return;
    seen.add(k);
    subChips.push(s);
  });

  const specStyle = (raw.subCategories || []).join(", ");
  const rawWithSpecs = raw as DoorRange & {
    thicknessMm?: number;
    specs?: { thicknessMm?: number };
  };
  const thicknessSrc = rawWithSpecs.thicknessMm ?? rawWithSpecs.specs?.thicknessMm;
  const specs = {
    material:  raw.material || "",
    finish:    raw.finishType || "",
    style:     specStyle,
    thickness: thicknessSrc ? `${thicknessSrc}mm` : "",
  };

  const supplierId = (raw.supplierId || "").toLowerCase();

  return {
    id:           raw.id || raw.sku || raw.name,
    name:         raw.name,
    supplierId,
    supplierLabel: supplierLabels?.get(supplierId) ?? "",
    description:  raw.description || "",
    finishType:   raw.finishType || "",
    priceGroup:   raw.priceGroup || "",
    subChips,
    tagPool: tagPoolLc,
    hero: heroImage,
    closeUp: defaultCloseUp,
    colours,
    paintToOrder: raw.paintToOrder || null,
    specs,
  };
}

function rangeHasTag(r: NormalisedRange, label: string): boolean {
  const key = label.toLowerCase().replace(/[^a-z0-9]+/g, "");
  return r.tagPool.some((t) => t.includes(key));
}

function filterRanges(ranges: NormalisedRange[], local: BrowserState): NormalisedRange[] {
  const q = local.search.trim().toLowerCase();
  return ranges.filter((r) => {
    if (local.supplierSel && (r.supplierId || "").toLowerCase() !== local.supplierSel) {
      return false;
    }
    if (local.styleSel.size > 0) {
      const any = [...local.styleSel].some((s) => rangeHasTag(r, s));
      if (!any) return false;
    }
    if (local.finishSel.size > 0) {
      const any = [...local.finishSel].some((f) => rangeHasTag(r, f));
      if (!any) return false;
    }
    if (q) {
      const hay = [
        r.name,
        r.description,
        r.finishType,
        ...r.subChips,
        ...r.colours.map((c) => c.name),
      ].join(" ").toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

function pill(label: string, on: boolean): string {
  return `<button type="button" class="kos-tkc-pill ${on ? "kos-active" : ""}" data-pill="${escapeAttr(label)}">${escape(label)}</button>`;
}

/** Pill with its icon above the label — for the visual attribute filters. Falls
 *  back to a plain pill if an icon is ever missing, so adding a new filter
 *  value can't leave a blank tile. */
function iconPill(label: string, on: boolean, icons: Record<string, string>): string {
  const glyph = icons[label];
  if (!glyph) return pill(label, on);
  return `<button type="button" class="kos-tkc-pill kos-tkc-pill--icon ${on ? "kos-active" : ""}" data-pill="${escapeAttr(label)}">
    ${glyph}<span>${escape(label)}</span>
  </button>`;
}

function toggleSet(set: Set<string>, v: string): void {
  if (set.has(v)) set.delete(v); else set.add(v);
}

function renderGrid(host: HTMLElement, api: RangeBrowserHost, local: BrowserState): void {
  const gridHost = host.querySelector<HTMLElement>("[data-role=grid-host]")!;
  const countEl = host.querySelector<HTMLElement>("[data-role=count]")!;
  // Per-supplier counts now live on the tabs themselves (renderSupplierTabs),
  // so there is no single fixed count element to update here any more.

  if (local.loading) {
    gridHost.innerHTML = `<div class="kos-tkc-loading">Loading kitchen styles…</div>`;
    countEl.textContent = "";
    return;
  }
  if (local.error) {
    gridHost.innerHTML = `<div class="kos-tkc-error">We couldn't load the live catalogue right now (${escape(local.error)}). You can still continue and add details on the next step.</div>`;
    countEl.textContent = "";
    return;
  }

  const filtered = filterRanges(local.ranges, local);
  const total = local.ranges.length;
  if (filtered.length === 0) {
    gridHost.innerHTML = `<div class="kos-tkc-empty">No door ranges match your filters.</div>`;
    countEl.textContent = total > 0 ? `Showing 0 of ${total} ranges (filtered)` : "";
    return;
  }
  countEl.textContent = filtered.length === total
    ? `Showing ${total} range${total === 1 ? "" : "s"}`
    : `Showing ${filtered.length} of ${total} ranges (filtered)`;

  gridHost.innerHTML = `<div class="kos-tkc-grid" data-role="grid"></div>`;
  const grid = gridHost.querySelector<HTMLElement>("[data-role=grid]")!;
  filtered.forEach((r) => grid.appendChild(buildCard(r, api)));
}

// ── Selection helper (shared by card + lightbox) ─────────────────────────────

/** Apply a range selection: mark the card, update wizard state, emit the
 *  `range_selected` analytics event. Used by the card's "Select This Style"
 *  button AND the matching button inside the lightbox. */
function applyRangeSelection(
  card: HTMLElement,
  range: NormalisedRange,
  colourName: string,
  api: RangeBrowserHost,
): void {
  const grid = card.parentElement;
  if (grid) {
    grid.querySelectorAll<HTMLElement>(".kos-tkc-card").forEach((c) => {
      c.classList.remove("kos-selected");
      const sb = c.querySelector<HTMLButtonElement>(".kos-tkc-select-btn");
      if (sb) sb.textContent = "Select This Style";
    });
  }
  card.classList.add("kos-selected");
  const selfBtn = card.querySelector<HTMLButtonElement>(".kos-tkc-select-btn");
  if (selfBtn) selfBtn.textContent = "✓ Selected";

  api.selectRange?.(range, colourName);

  track({
    eventType: "range_selected",
    rangeId: range.id,
    rangeName: range.name,
    ...(colourName ? { colourName } : {}),
    ...(range.supplierId ? { supplierId: range.supplierId } : {}),
  });
}

// ── Card builder (DOM, mirroring stage tool-app:2380) ────────────────────────

function buildCard(range: NormalisedRange, api: RangeBrowserHost): HTMLElement {
  const selected = api.getSelection().rangeId === range.id;
  const card = document.createElement("div");
  card.className = "kos-tkc-card" + (selected ? " kos-selected" : "");
  card.dataset.kosRangeId = range.id;
  card.dataset.kosRangeName = range.name;

  // Active colour starts at the previously-chosen one if this is the selected
  // card, otherwise the range's first colour (matches stage).
  let activeIdx = 0;
  if (selected && api.getSelection().colourName) {
    const found = range.colours.findIndex((c) => c.name === api.getSelection().colourName);
    if (found !== -1) activeIdx = found;
  }

  // ── Hero ──
  const hero = document.createElement("div");
  hero.className = "kos-tkc-hero";
  const heroImg = document.createElement("img");
  heroImg.alt = `${range.name} lifestyle photo`;
  heroImg.loading = "lazy";
  heroImg.src = range.colours[activeIdx]?.hero || range.hero || "";
  hero.appendChild(heroImg);
  if (range.priceGroup) {
    const price = document.createElement("div");
    price.className = "kos-tkc-price-pill";
    price.textContent = range.priceGroup;
    hero.appendChild(price);
  }
  const zoom = document.createElement("div");
  zoom.className = "kos-tkc-zoom-hint";
  zoom.textContent = "⌖ Enlarge";
  hero.appendChild(zoom);
  hero.addEventListener("click", () => {
    const imgs = range.colours
      .map((c) => ({ name: `${range.name}${c.name ? ` — ${c.name}` : ""}`, img: c.hero }))
      .filter((x) => !!x.img);
    let startIdx = 0;
    for (let i = 0; i < imgs.length; i++) {
      if (imgs[i] && imgs[i]!.img === heroImg.src) { startIdx = i; break; }
    }
    openLightbox(imgs, startIdx, api.selectRange && {
      onSelect: () => {
        const chosenColour = range.colours[activeIdx]?.name || api.getSelection().colourName || "";
        applyRangeSelection(card, range, chosenColour, api);
        api.afterSelect?.();
      },
    });
    track({
      eventType: "range_image_viewed",
      rangeId: range.id,
      rangeName: range.name,
      ...(range.supplierId ? { supplierId: range.supplierId } : {}),
    });
  });
  card.appendChild(hero);

  // Track the card scrolling into view (first time only).
  observeRangeCard(card, { id: range.id, name: range.name, supplierId: range.supplierId });

  // ── Swatch bar ──
  const swatchBar = document.createElement("div");
  swatchBar.className = "kos-tkc-swatch-bar";
  const swatchLabel = document.createElement("span");
  swatchLabel.className = "kos-tkc-swatch-bar-label";
  swatchLabel.textContent = "Colours:";
  swatchBar.appendChild(swatchLabel);

  const swatchRow = document.createElement("div");
  swatchRow.className = "kos-tkc-swatches";
  const curLabel = document.createElement("span");
  curLabel.className = "kos-tkc-cur-colour";
  curLabel.textContent = range.colours[activeIdx]?.name || "";

  const closeUpImg = document.createElement("img");
  closeUpImg.alt = `${range.name} door close-up`;
  closeUpImg.loading = "lazy";
  closeUpImg.src = range.colours[activeIdx]?.closeUp || range.closeUp || "";
  closeUpImg.addEventListener("click", (e) => {
    e.stopPropagation();
    openLightbox([{ name: `${range.name} — door close-up`, img: closeUpImg.src }], 0, api.selectRange && {
      onSelect: () => {
        const chosenColour = range.colours[activeIdx]?.name || api.getSelection().colourName || "";
        applyRangeSelection(card, range, chosenColour, api);
        api.afterSelect?.();
      },
    });
    track({
      eventType: "range_image_viewed",
      rangeId: range.id,
      rangeName: range.name,
      ...(range.supplierId ? { supplierId: range.supplierId } : {}),
    });
  });

  range.colours.forEach((c, idx) => {
    if (!c.hex && !c.hero) return;
    const sw = document.createElement("button");
    sw.type = "button";
    sw.className = "kos-tkc-swatch" + (idx === activeIdx ? " kos-active" : "");
    sw.style.background = c.hex || "#cbd5e1";
    sw.title = c.name;
    sw.setAttribute("aria-label", c.name);
    if (c.hex && /^#(f|F|e|E)/.test(c.hex)) {
      sw.style.boxShadow = "0 1px 3px rgba(0,0,0,.18), inset 0 0 0 1px rgba(0,0,0,.12)";
    }
    sw.addEventListener("click", (e) => {
      e.stopPropagation();
      e.preventDefault();
      // Track the clicked swatch so "Select This Style" (which reads
      // range.colours[activeIdx]) stores THIS colour, not the range's first.
      activeIdx = idx;
      swatchRow.querySelectorAll<HTMLElement>(".kos-tkc-swatch").forEach((s) => s.classList.remove("kos-active"));
      sw.classList.add("kos-active");
      curLabel.textContent = c.name || "";
      if (c.hero && heroImg.src !== c.hero) {
        heroImg.style.opacity = "0";
        setTimeout(() => {
          heroImg.src = c.hero;
          heroImg.alt = `${range.name} — ${c.name}`;
          heroImg.style.opacity = "1";
        }, 130);
      }
      if (c.closeUp && closeUpImg.src !== c.closeUp) {
        closeUpImg.style.opacity = "0";
        setTimeout(() => {
          closeUpImg.src = c.closeUp;
          closeUpImg.alt = `${range.name} — ${c.name} close-up`;
          closeUpImg.style.opacity = "1";
        }, 130);
      }
      // If this card is the selected card, persist the new colour.
      if (api.getSelection().rangeId === range.id) {
        api.changeColour?.(range, c.name);
      }
      track({
        eventType: "colour_selected",
        rangeId: range.id,
        rangeName: range.name,
        colourName: c.name,
        ...(range.supplierId ? { supplierId: range.supplierId } : {}),
      });
    });
    swatchRow.appendChild(sw);
  });
  swatchBar.appendChild(swatchRow);
  swatchBar.appendChild(curLabel);
  card.appendChild(swatchBar);

  // ── Right-side wrapper (body + select) ──
  const cardRight = document.createElement("div");
  cardRight.className = "kos-tkc-card-right";

  const body = document.createElement("div");
  body.className = "kos-tkc-body";

  const bodyLeft = document.createElement("div");
  bodyLeft.className = "kos-tkc-body-left";

  const nm = document.createElement("div");
  nm.className = "kos-tkc-name";
  nm.textContent = range.name;
  bodyLeft.appendChild(nm);

  if (range.subChips.length > 0) {
    const chips = document.createElement("div");
    chips.className = "kos-tkc-chips";
    range.subChips.forEach((s) => {
      const chip = document.createElement("span");
      chip.className = "kos-tkc-chip";
      chip.textContent = s;
      chips.appendChild(chip);
    });
    bodyLeft.appendChild(chips);
  }

  if (range.description) {
    const desc = document.createElement("div");
    desc.className = "kos-tkc-desc";
    desc.textContent = range.description;
    bodyLeft.appendChild(desc);
  }

  const colourTotal = range.colours.filter((c) => !!c.hex || !!c.name).length;
  const ptoNote = range.paintToOrder && range.paintToOrder.enabled
    ? (range.paintToOrder.label || "Paint to Order available")
    : "";
  const meta = document.createElement("div");
  meta.className = "kos-tkc-meta";
  meta.textContent = `${colourTotal} colour${colourTotal === 1 ? "" : "s"}`
    + (ptoNote ? ` · ${ptoNote}` : "");
  bodyLeft.appendChild(meta);

  // View Specs accordion
  const specsBtn = document.createElement("button");
  specsBtn.type = "button";
  specsBtn.className = "kos-tkc-specs-toggle";
  specsBtn.textContent = "View Specs ▾";
  const specsPanel = document.createElement("div");
  specsPanel.className = "kos-tkc-specs";
  const specRows: Array<[string, string]> = [
    ["Material",  range.specs.material],
    ["Finish",    range.specs.finish],
    ["Style",     range.specs.style],
    ["Thickness", range.specs.thickness],
  ];
  specRows.forEach(([label, value]) => {
    if (!value) return;
    const row = document.createElement("div");
    row.className = "kos-tkc-spec-row";
    const lab = document.createElement("span");
    lab.className = "kos-tkc-spec-label";
    lab.textContent = label;
    const val = document.createElement("span");
    val.className = "kos-tkc-spec-value";
    val.textContent = value;
    row.appendChild(lab);
    row.appendChild(val);
    specsPanel.appendChild(row);
  });
  specsBtn.addEventListener("click", (e) => {
    e.stopPropagation();
    const isOpen = specsPanel.classList.toggle("kos-open");
    specsBtn.textContent = "View Specs " + (isOpen ? "▴" : "▾");
  });
  bodyLeft.appendChild(specsBtn);
  bodyLeft.appendChild(specsPanel);
  body.appendChild(bodyLeft);

  // Close-up column
  const closeUpCol = document.createElement("div");
  closeUpCol.className = "kos-tkc-closeup";
  const closeUpLabel = document.createElement("div");
  closeUpLabel.className = "kos-tkc-closeup-label";
  closeUpLabel.textContent = "Door close-up";
  closeUpCol.appendChild(closeUpImg);
  closeUpCol.appendChild(closeUpLabel);
  body.appendChild(closeUpCol);

  cardRight.appendChild(body);

  // Select CTA — only when the host can actually take a selection. A browse-only
  // page ("Our Kitchens") passes no selectRange, and gets a pure catalogue.
  if (api.selectRange) {
    const selectBtn = document.createElement("button");
    selectBtn.type = "button";
    selectBtn.className = "kos-tkc-select-btn";
    selectBtn.textContent = selected ? "✓ Selected" : "Select This Style";
    selectBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      const chosenColour = range.colours[activeIdx]?.name || api.getSelection().colourName || "";
      applyRangeSelection(card, range, chosenColour, api);
      // Both a style and (default-or-chosen) colour are now committed — slide the
      // customer down to the Worktops/Storage/Appliances options so they don't
      // have to hunt for the next step. (The lightbox select paths already do this.)
      api.afterSelect?.();
    });
    cardRight.appendChild(selectBtn);
  }

  card.appendChild(cardRight);
  return card;
}

// ── Lightbox ────────────────────────────────────────────────────────────────

interface LightboxImg { name: string; img: string }
interface LightboxSelectAction {
  /** Called when the user hits the Select-This-Style CTA inside the lightbox.
   *  The lightbox closes itself immediately after invoking this — callers
   *  shouldn't trigger close() themselves. */
  onSelect: () => void;
}
function openLightbox(
  imgs: LightboxImg[],
  startIdx: number,
  // `false | undefined` from a `cond && {…}` guard reads as "no select CTA".
  selectAction?: LightboxSelectAction | false,
): void {
  if (imgs.length === 0) return;
  const safeStart = Math.max(0, Math.min(startIdx, imgs.length - 1));
  let idx = safeStart;
  const ovl = document.createElement("div");
  ovl.className = "wiz-lightbox kos-tkc-lightbox";
  ovl.innerHTML = `
    <button class="wiz-lightbox__close" aria-label="Close">&times;</button>
    <button class="kos-tkc-lb-nav kos-tkc-lb-prev" aria-label="Previous">&#8249;</button>
    <div class="kos-tkc-lb-stage">
      <img alt="" />
      ${selectAction ? `<button type="button" class="kos-tkc-lb-select">Select This Style</button>` : ""}
    </div>
    <button class="kos-tkc-lb-nav kos-tkc-lb-next" aria-label="Next">&#8250;</button>
  `;
  const imgEl = ovl.querySelector<HTMLImageElement>("img")!;
  const prevBtn = ovl.querySelector<HTMLButtonElement>(".kos-tkc-lb-prev")!;
  const nextBtn = ovl.querySelector<HTMLButtonElement>(".kos-tkc-lb-next")!;
  const stageEl = ovl.querySelector<HTMLElement>(".kos-tkc-lb-stage")!;
  const selectEl = ovl.querySelector<HTMLButtonElement>(".kos-tkc-lb-select");
  function update(): void {
    const current = imgs[idx];
    if (!current) return;
    imgEl.src = current.img;
    imgEl.alt = current.name;
    prevBtn.style.visibility = imgs.length > 1 ? "" : "hidden";
    nextBtn.style.visibility = imgs.length > 1 ? "" : "hidden";
  }
  function close(): void { ovl.remove(); document.removeEventListener("keydown", onKey); }
  function onKey(e: KeyboardEvent): void {
    if (e.key === "Escape") close();
    else if (e.key === "ArrowLeft")  { idx = (idx - 1 + imgs.length) % imgs.length; update(); }
    else if (e.key === "ArrowRight") { idx = (idx + 1) % imgs.length; update(); }
  }
  prevBtn.addEventListener("click", (e) => {
    e.stopPropagation();
    idx = (idx - 1 + imgs.length) % imgs.length;
    update();
  });
  nextBtn.addEventListener("click", (e) => {
    e.stopPropagation();
    idx = (idx + 1) % imgs.length;
    update();
  });
  // Clicks inside the stage (image + select button) shouldn't dismiss the
  // lightbox. Only backdrop clicks + the explicit close button close it.
  stageEl.addEventListener("click", (e) => { e.stopPropagation(); });
  ovl.addEventListener("click", (e) => {
    const t = e.target as HTMLElement;
    if (t.tagName !== "IMG" && !t.classList.contains("kos-tkc-lb-nav")) close();
  });
  ovl.querySelector<HTMLButtonElement>(".wiz-lightbox__close")!
    .addEventListener("click", close);
  if (selectEl && selectAction) {
    selectEl.addEventListener("click", (e) => {
      e.stopPropagation();
      close();
      // Run the caller's handler after close so any smooth-scroll target is
      // measured against the now-restored body scroll context.
      try { selectAction.onSelect(); }
      catch (err) { console.warn("[lightbox] onSelect threw:", err); }
    });
  }
  document.addEventListener("keydown", onKey);
  update();
  document.body.appendChild(ovl);
}
